/**
 * uploadFilePlugin.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { Command, Editor, Plugin, ClipboardPipeline, FileRepository,
  FileDialogButtonView, toWidget } from "ckeditor5";

import uploadIcon from "./upload-solid.svg";
import { SCUploaderAdapterOptions, removeFileFromGroup } from "./uploadAdapter";
import { StoredFile, classForType } from "../models/file";

const FILE_BOX = "fileBox";
const MAIN_CLASS_NAME = "file-attachment-box";

export class UploadFilePlugin extends Plugin {
  static get pluginName(): string { return "UploadFilePlugin"; }
  static get requires(): Array<any> { return [FileRepository, ClipboardPipeline]; }

  fileRepository: FileRepository;
  _submitted: boolean;

  set submitted(sub: boolean) {
    this._submitted = sub;
  }

  constructor(editor: Editor) {
    super(editor);
    this._submitted = false;

    this.dataDowncastConverter = this.dataDowncastConverter.bind(this);
    this.fileRepository = editor.plugins.get("FileRepository");
  }

  init(): void {
    const uploadFileCommand = new UploadFileCommand(this.editor);

    this.editor.commands.add("uploadFile", uploadFileCommand);

    this.editor.model.schema.register(FILE_BOX, {
      allowWhere: "$block",
      isObject: true,
      allowAttributes: ["filePath", "fileName", "fileSize", "fileType",
        "keyId", "fileId", "groupId",
        "uploadId", "uploadStatus", "uploadPercent"]
    });

    this.editor.conversion.for("upcast").add(
      (dispatcher: any) => dispatcher.on("element:div", this.buildModel));

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: {
        name: FILE_BOX,
        attributes: ["fileId", "uploadStatus", "uploadPercent"],
        children: true
      },
      view: (fileboxModel: any, conversion: any) => {
        const [fileBoxView, fileIconView, fileTitleView, statusView] = this.buildView(true, {item: fileboxModel}, conversion);

        this._submitted = false;
        conversion.writer.insert(
          conversion.writer.createPositionAt(fileBoxView, 0),
          fileIconView
        );

        conversion.writer.insert(
          conversion.writer.createPositionAfter(fileIconView),
          fileTitleView
        );
        conversion.writer.insert(
          conversion.writer.createPositionAfter(fileTitleView),
          statusView
        );

        return toWidget(fileBoxView, conversion.writer, {label: "file attachment"});
      }
    });
    this.editor.conversion.for("dataDowncast").add(
      (dispatcher: any) => dispatcher.on("insert:" + FILE_BOX, this.dataDowncastConverter));

    this.editor.plugins.get("ClipboardPipeline").on("inputTransformation", (_evt: any, data: any) => {
      const dataTransfer = data.dataTransfer as DataTransfer;
      const imageTest = new RegExp("^image/(jpeg|png|gif|bmp|webp|tiff)$");
      for (const file of dataTransfer.files) {
        if(!imageTest.test(file.type)) {
          addFileToEditor(file, this.editor, {});
        }
      }
    });

    this.editor.model.document.on("change", () => {
      const changes = this.editor.model.document.differ.getChanges({includeChangesInGraveyard: true}).reverse();
      const insertedFileIds = new Set();

      for (const entry of changes) {
        if (entry.type !== "insert" || entry.name === "$text") { continue; }
        const item = entry.position.nodeAfter;
        if (!item) { continue; }
        const isInGraveyard = (entry.position.root as any).rootName == "$graveyard";

        for (const value of this.editor.model.createRangeOn(item)) {
          if (!value.item.is("element", FILE_BOX)) { continue; }
          const uploadId = value.item.getAttribute("uploadId") as string | number | undefined;
          if (!uploadId) { continue; }

          if (isInGraveyard) {
            if (!uploadId || !insertedFileIds.has(uploadId)) {
              this.removeFile(value.item);
            }
          } else {
            insertedFileIds.add(uploadId);
            const loader = this.fileRepository.loaders.get(uploadId);
            if (!loader) { console.log("no loader for upload"); continue; }
            if (loader.status === "idle") {
              this.uploadFile(loader, value.item);
            }
          }
        }
      }
    });
  }

  private buildModel(_event: any, data: any, conversion: any) {
    const fileBoxView = data.viewItem;
    if (!fileBoxView.hasClass(MAIN_CLASS_NAME)) { return; }

    const fileboxModel = conversion.writer.createElement(FILE_BOX, {
      fileId: fileBoxView.getAttribute("data-file-id"),
      keyId: fileBoxView.getAttribute("data-key-id"),
      groupId: fileBoxView.getAttribute("data-group-id"),
      fileName: fileBoxView.getAttribute("data-file-name"),
      fileSize: fileBoxView.getAttribute("data-file-size"),
      filePath: fileBoxView.getAttribute("data-path"),
      fileType: fileBoxView.getAttribute("data-file-type"),
      uploadStatus: "final"
    });

    if (!conversion.safeInsert(fileboxModel, data.modelCursor)) {
      return;
    }

    conversion.consumable.consume(fileBoxView, {name: true});
    for (const child of fileBoxView.getChildren()) {
      conversion.consumable.consume(child, {name: true});
    }

    conversion.updateConversionResult(fileboxModel, data);
  }

  private dataDowncastConverter (_event: any, data: any, conversion: any) {
    const [fileBoxView, fileIconView, fileTitleView, statusView] = this.buildView(false, data, conversion);
    this.insertViews(data, conversion, fileBoxView, fileIconView, fileTitleView, statusView);
  }

  private buildView(editing: boolean, data: any, conversion: any): [any, any, any, any] {
    const fileboxModel = data.item;
    const options = this.editor.config.get("scUpload") as SCUploaderAdapterOptions;
    const fileMimeType = fileboxModel.getAttribute("fileType");
    const typeClasses = classForType(fileMimeType);

    const attrs: any = {
      class: MAIN_CLASS_NAME,
      "data-file-id": fileboxModel.getAttribute("fileId"),
      "data-key-id": fileboxModel.getAttribute("keyId"),
      "data-group-id": fileboxModel.getAttribute("groupId") || options.groupID(),
      "data-file-name": fileboxModel.getAttribute("fileName"),
      "data-file-size": fileboxModel.getAttribute("fileSize"),
      "data-file-type": fileboxModel.getAttribute("fileType"),
      "data-path": fileboxModel.getAttribute("filePath"),
      "data-upload-status": fileboxModel.getAttribute("uploadStatus")
    };
    if (editing) {
      attrs["data-upload-id"] = fileboxModel.getAttribute("uploadId");
    }

    const fileBoxView = conversion.writer.createContainerElement("div", attrs);
    const fileIconView = conversion.writer.createUIElement("span", {class: `fa-solid ${typeClasses}`})
    const fileTitleView = conversion.writer.createUIElement("div", {class: "file-attachment-name"}, function (this: any, domDoc: any) {
      const elem = this.toDomElement(domDoc);
      elem.innerText = fileboxModel.getAttribute("fileName");
      return elem;
    });

    const percent = fileboxModel.getAttribute("uploadPercent");
    const status = fileboxModel.getAttribute("uploadStatus") as "idle" | "uploading" | "complete" | "final" | "invalid";
    const statusView = conversion.writer.createRawElement("div",
        {class: "upload-status"}, function (domElement: HTMLDivElement) {
      options.statusRenderer(domElement, editing ? status : "final", percent);
    });

    return [fileBoxView, fileIconView, fileTitleView, statusView];
  }

  private insertViews(data: any, conversion: any,
      fileBoxView: any,
      fileIconView: any,
      fileTitleView: any,
      statusView: any) {
    //const uploadId = data.item.getAttribute("uploadId");
    conversion.consumable.consume(data.item, "insert");
    conversion.writer.insert(
      conversion.mapper.toViewPosition(data.range.start),
      fileBoxView
    );
    conversion.mapper.bindElements(data.item, fileBoxView);

    conversion.writer.insert(
      conversion.writer.createPositionAt(fileBoxView, 0),
      fileIconView
    );
    conversion.writer.insert(
      conversion.writer.createPositionAfter(fileIconView),
      fileTitleView
    );

    conversion.writer.insert(
      conversion.writer.createPositionAfter(fileTitleView),
      statusView
    );
    //conversion.mapper.bindElementToMarker(statusView, `status-${uploadId}`);
  }

  private async uploadFile (loader: any, fileboxModel: any) {
    const options = this.editor.config.get("scUpload") as SCUploaderAdapterOptions;
    try {
      loader.on("change:uploadedPercent", (_event: any, _name: any, value: any) => {
        this.editor.model.enqueueChange({isUndoable: false}, (writer: any) => {
          writer.setAttribute("uploadPercent", value, fileboxModel);
        });
      });
      options.uploadEvent("start", loader.id, "");
      this.editor.model.enqueueChange({isUndoable: false}, (writer: any) => {
        writer.setAttribute("uploadStatus", "uploading", fileboxModel);
      });
      const resp = await loader.upload() as StoredFile;

      this.editor.model.enqueueChange({isUndoable: false}, (writer: any) => {
        writer.setAttribute("uploadStatus", "complete", fileboxModel);
        writer.setAttribute("fileId", resp._id, fileboxModel);
        writer.setAttribute("filePath", resp.path, fileboxModel);
        writer.setAttribute("keyId", resp.keyID, fileboxModel);
      });
      options.uploadEvent("complete", loader.id, "");
    } catch (err) {
      if (err === "aborted") {
        console.log("aborted:", err);
        options.uploadEvent("error", loader.id, "");
      } else {
        console.error("Error uploading file:", err);
        this.editor.model.enqueueChange({isUndoable: false}, (writer: any) => {
          writer.setAttribute("uploadStatus", "invalid", fileboxModel);
        });
        options.uploadEvent("error", loader.id, (err as Error).message);
      }
    } finally {
      this.fileRepository.destroyLoader(loader);
    }
  }

  private async removeFile(fileboxModel: any) {
    if (this._submitted) {
      console.log("ignoring remove. we submitted this file");
      return;
    }
    const uploadStatus = fileboxModel.getAttribute("uploadStatus");
    const fileID = fileboxModel.getAttribute("fileId");
    const uploadId = fileboxModel.getAttribute("uploadId");
    const groupId = fileboxModel.getAttribute("groupId");
    const options = this.editor.config.get("scUpload") as SCUploaderAdapterOptions;

    if (uploadStatus === "final") {
      // "final" here would mean this HTML was previously pasted into the editor
      return;
    }

    if (uploadId && uploadStatus !== "complete" && uploadStatus !== "invalid") {
      const loader = this.fileRepository.loaders.get(uploadId);
      if (loader) {
        (loader as any)._filePromiseWrapper = {isFulfilled: true};
        loader.abort();
      }
    }
    if (fileID) {
      removeFileFromGroup(groupId, fileID);
    }

    options.uploadEvent("removed", uploadId, "");
  }
}

function addFileToEditor (file: File, editor: any, attributes: any, position?: any) {
  const fileRepository = editor.plugins.get( FileRepository );
  const options = editor.config.get("scUpload") as SCUploaderAdapterOptions;
	const loader = fileRepository.createLoader(file);
  if (!loader) { return; }

  loader.nonImage = true;

  attributes = Object.assign(attributes, {
    uploadId: loader.id,
    groupId: options.groupID(),
    uploadStatus: "idle",
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type || "application/octet-stream",
    filePath: ""
  });

  for (const name in attributes) {
    if (!editor.model.schema.checkAttribute(FILE_BOX, name)) {
      delete attributes[name];
    }
  }

  editor.model.change( (writer: any) => {
    const fileBoxElement = writer.createElement(FILE_BOX, attributes);

    editor.model.insertContent(fileBoxElement, position);
    if (fileBoxElement.parent) {
      writer.setSelection(fileBoxElement, "on");
      return fileBoxElement;
    }
    return null;
  });
}

export class UploadFileCommand extends Command {
  execute (options: {file: File | File[]}): void {
    const files = Array.isArray( options.file ) ? options.file : [ options.file ] as Array<File>;
		const selection = this.editor.model.document.selection;
    const attrEntries = selection.getAttributes();
    const selectionAttributes: any = {};

    for (const [key, value] of attrEntries) {
      selectionAttributes[key] = value;
    }

    files.forEach((file: File, index: number) => {
      const selectedElement = selection.getSelectedElement();

      if (index && selectedElement && (selectedElement as any).hasClass(MAIN_CLASS_NAME)) {
        const position = this.editor.model.createPositionAfter(selectedElement);
        addFileToEditor(file, this.editor, selectionAttributes, position);
      } else {
        addFileToEditor(file, this.editor, selectionAttributes);
      }
    });
  }
}

export class UploadFilePluginUI extends Plugin {
  init(): void {
    this.editor.ui.componentFactory.add("uploadFile", (locale: any) => {
      const commandName = "uploadFile";
      const command = this.editor.commands.get(commandName);

      const view = new FileDialogButtonView( locale );

      view.buttonView.set( {
          label: this.editor.t("Upload File"),
          icon: uploadIcon,
          tooltip: true,
          class: "upload-btn"
      } );

      (view.buttonView as any).bind( "isOn", "isEnabled" ).to( command, "value", "isEnabled" );

      view.on("done", (_evt: any, files: Array<File>) => {
        for (const file of files) {
          this.editor.execute(commandName, {file: file});
        }
      });

      return view;
    });
  }
}

/*function humanReadableBytes(bytes: number){
  const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
   let l = 0, n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
   while(n >= 1024 && ++l){
       n = n/1024;
   }
   return(n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l]);
 }*/
